import React, { useState, useEffect } from 'react';
import { ShiftProvider, useShift } from './contexts/ShiftContext';
import ShiftSetup from './components/ShiftSetup';
import ShiftComplete from './components/ShiftComplete';
import MainApp from './components/MainApp';
import LockScreen from './components/LockScreen';
import LegalAcknowledge from './components/LegalAcknowledge';
import LegalPage from './components/LegalPage';
import Landing from './components/landing/Landing';
import DictationConsent from './components/DictationConsent';
import GetTheApp from './components/GetTheApp';
import AndroidTestDownload from './components/AndroidTestDownload';
import { LANGUAGES } from './data/translations';
import { LEGAL_VERSION } from './data/legal';
import './App.css';

const LEGAL_ACK_KEY = 'shiftready:legal-ack';
// The full app is subscription-only on iPhone/Android; /app on the web shows a download page.
const WEB_APP_ENABLED = false;

// Public static-route table — these URLs must work BEFORE legal-ack and
// without entering a shift so Apple + Google reviewers can crawl them.
const PUBLIC_ROUTES = {
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/disclaimer': 'disclaimer',
};

function AppShell() {
  const { shift, complete, lang, locked } = useShift();
  const rtl = LANGUAGES.find((l) => l.code === lang)?.rtl;
  const [acknowledged, setAcknowledged] = useState(() => {
    try {
      const raw = localStorage.getItem(LEGAL_ACK_KEY);
      if (!raw) return false;
      const v = JSON.parse(raw);
      return v && v.version === LEGAL_VERSION;
    } catch { return false; }
  });

  const accept = () => {
    localStorage.setItem(LEGAL_ACK_KEY, JSON.stringify({ version: LEGAL_VERSION, ts: Date.now() }));
    setAcknowledged(true);
  };

  // Legal ack is required before EVERYTHING else
  let content;
  if (!acknowledged) content = <LegalAcknowledge onAccept={accept} />;
  else if (complete) content = <ShiftComplete />;
  else if (shift && locked) content = <LockScreen />;
  else if (shift) content = <MainApp />;
  else content = <ShiftSetup />;

  return (
    <div className="App" dir={rtl ? 'rtl' : 'ltr'} data-testid="shiftready-app">
      {content}
      <DictationConsent />
    </div>
  );
}

export default function App() {
  // Short-circuit for public legal URLs — crawler + reviewer-friendly, no
  // ShiftProvider, no legal-ack gate, no local storage access.
  const [path, setPath] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') || '/' : '/'
  );
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname.replace(/\/$/, '') || '/');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const publicKind = PUBLIC_ROUTES[path];
  if (publicKind) return <LegalPage kind={publicKind} />;
  if (path === '/') return <Landing />;
  if (path === '/android-test') return <AndroidTestDownload />;
  if (!WEB_APP_ENABLED) return <GetTheApp />;

  return (
    <ShiftProvider>
      <AppShell />
    </ShiftProvider>
  );
}
